import { useEffect, useMemo, useRef, useState } from 'react';
import { toDateStr } from '../../utils';
import s from '../../styles/analytics.module.css';

// Shared date-range presets (same set the analytics panel uses). `Today` is
// opt-in: most reports read oddly over a single day, but an exception list is
// exactly the place to ask "what is waiting right now".
const TODAY_RANGE = { id: 'today', label: 'Today' };

const RANGES = [
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: '1w', label: '1W', days: 7 },
  { id: '1m', label: '1M', days: 30 },
  { id: '3m', label: '3M', days: 90 },
  { id: '6m', label: '6M', days: 180 },
  { id: '1y', label: '1Y', days: 365 },
];

function rangeToDates(rangeId) {
  const now = new Date();
  if (rangeId === 'today') {
    const t = toDateStr(now);
    return { start: t, end: t };
  }
  if (rangeId === 'thisMonth') {
    return {
      start: toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)),
      end:   toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  if (rangeId === 'lastMonth') {
    return {
      start: toDateStr(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      end:   toDateStr(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
  const days = RANGES.find(r => r.id === rangeId)?.days || 30;
  const endD = new Date();
  const startD = new Date();
  startD.setDate(startD.getDate() - (days - 1));
  return { start: toDateStr(startD), end: toDateStr(endD) };
}

export function DateRangeFilter({
  onChange, defaultRange, initialStart, initialEnd, includeToday = false,
}) {
  const today = toDateStr(new Date());
  const presets = includeToday ? [TODAY_RANGE, ...RANGES] : RANGES;
  // A page that offers Today opens on it: it asks for the preset because that
  // is the window it is about. An explicit `defaultRange` still wins.
  const firstRange = defaultRange || (includeToday ? 'today' : 'thisMonth');

  // Dates handed over by a link are shown as the preset that describes them
  // where one does. Arriving from a banner about this month should light up
  // "This Month", not drop into Custom with the same two dates spelled out.
  const handedMatch = (initialStart && initialEnd)
    ? presets.find((r) => {
        const d = rangeToDates(r.id);
        return d.start === initialStart && d.end === initialEnd;
      })
    : null;
  const handed = !!(initialStart && initialEnd) && !handedMatch;

  const [rangeId, setRangeId] = useState(handedMatch ? handedMatch.id : firstRange);
  const [custom, setCustom] = useState(handed);
  const [customStart, setCustomStart] = useState(handed ? initialStart : '');
  const [customEnd, setCustomEnd] = useState(handed ? initialEnd : '');

  const { start, end } = useMemo(() => {
    if (custom && customStart && customEnd) {
      return customStart <= customEnd
        ? { start: customStart, end: customEnd }
        : { start: customEnd, end: customStart };
    }
    return rangeToDates(rangeId);
  }, [custom, customStart, customEnd, rangeId]);

  // Notify the parent on range changes (ref keeps onChange out of the deps).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => { onChangeRef.current({ start, end }); }, [start, end]);

  const selectPreset = (id) => { setCustom(false); setRangeId(id); };
  const enableCustom = () => {
    if (!custom) { setCustomStart(start); setCustomEnd(end); }
    setCustom(true);
  };

  return (
    <div className={s.rangeControls}>
      <div className={s.rangeBtns} role="group" aria-label="Date range">
        {presets.map((r) => (
          <button
            key={r.id}
            className={`${s.rangeBtn} ${!custom && rangeId === r.id ? s.rangeBtnActive : ''}`}
            onClick={() => selectPreset(r.id)}
          >
            {r.label}
          </button>
        ))}
        <button className={`${s.rangeBtn} ${custom ? s.rangeBtnActive : ''}`} onClick={enableCustom}>
          Custom
        </button>
      </div>
      {custom && (
        <div className={s.customRange}>
          <input
            type="date"
            className={s.dateInput}
            value={customStart}
            max={customEnd || today}
            onChange={(e) => setCustomStart(e.target.value)}
            aria-label="Start date"
          />
          <span className={s.dateDash}>–</span>
          <input
            type="date"
            className={s.dateInput}
            value={customEnd}
            min={customStart}
            max={today}
            onChange={(e) => setCustomEnd(e.target.value)}
            aria-label="End date"
          />
        </div>
      )}
    </div>
  );
}
